import { useState } from "react";
import { Check, ChevronsUpDown, Plus } from "lucide-react";
import {
  useListAttributeKeys,
  useCreateAttributeKey,
  getListAttributeKeysQueryKey,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

interface AttributeKeyComboboxProps {
  value: number | null;
  onChange: (id: number) => void;
  placeholder?: string;
  disabled?: boolean;
  // 2026-05-19: hide options whose name (case-insensitive) appears here.
  // Used by item-specs-input to prevent the same key being picked twice.
  excludeNames?: string[];
}

export function AttributeKeyCombobox({
  value,
  onChange,
  placeholder = "Select attribute key…",
  disabled = false,
  excludeNames = [],
}: AttributeKeyComboboxProps) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: keys = [] } = useListAttributeKeys({ status: "Active" });

  const createMutation = useCreateAttributeKey();

  const selectedKey = keys.find((k) => k.id === value);

  const excludedSet = new Set(excludeNames.map((n) => n.toLowerCase()));
  const filteredKeys = keys.filter(
    (k) =>
      k.name.toLowerCase().includes(inputValue.toLowerCase()) &&
      // Always keep the currently-selected key visible so its label still renders
      // even if it's in the exclude list (defensive, shouldn't normally happen).
      (k.id === value || !excludedSet.has(k.name.toLowerCase())),
  );

  const exactMatch = keys.some(
    (k) => k.name.toLowerCase() === inputValue.toLowerCase()
  );

  const showAddNew = inputValue.trim().length > 0 && !exactMatch;

  async function handleAddNew() {
    try {
      const res = await createMutation.mutateAsync({
        data: { name: inputValue.trim(), status: "Active" },
      });
      queryClient.invalidateQueries({ queryKey: getListAttributeKeysQueryKey() });
      onChange(res.id);
      setInputValue("");
      setOpen(false);
      toast({ title: `Added attribute key '${res.name}'` });
    } catch (err: unknown) {
      const status = (err as { status?: number })?.status;
      if (status === 409) {
        toast({
          title: "Already exists",
          description: `Attribute key '${inputValue}' already exists.`,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Failed to add attribute key",
          variant: "destructive",
        });
      }
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
          disabled={disabled}
        >
          <span className={cn(!selectedKey && "text-muted-foreground")}>
            {selectedKey ? selectedKey.name : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search or type to add…"
            value={inputValue}
            onValueChange={setInputValue}
          />
          <CommandList>
            <CommandEmpty>No attribute keys found.</CommandEmpty>
            <CommandGroup>
              {filteredKeys.map((key) => (
                <CommandItem
                  key={key.id}
                  value={String(key.id)}
                  onSelect={() => {
                    onChange(key.id);
                    setInputValue("");
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === key.id ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {key.name}
                </CommandItem>
              ))}
            </CommandGroup>
            {showAddNew && (
              <CommandGroup>
                <CommandItem
                  value={`__create_${inputValue}`}
                  onSelect={handleAddNew}
                  disabled={createMutation.isPending}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add &ldquo;{inputValue.trim()}&rdquo; as new attribute key
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
